import type { MouseEvent } from 'react';

/**
 * Backdrop behavior for our modals.
 *
 * These modals intentionally do **NOT** close on a backdrop click. They are
 * data-entry surfaces (quote builder, proforma, commercial invoice, new RFQ,
 * etc.) where losing in-progress work to a stray click is costly. Two earlier
 * attempts at a "drag-safe" click-outside still leaked:
 *   - a text-selection drag that releases on the backdrop fired a click there;
 *   - in the quote / edit-quote modals the embedded <iframe> preview swallows
 *     mouse events, so a press starting over it is invisible to the parent and
 *     the mousedown-origin guard goes stale → the modal closed on what felt
 *     like just moving the mouse off the modal.
 *
 * So we stop trying to be clever: the backdrop only dims. Closing is explicit —
 * the header ✕ or the Cancel/Done button (every modal has both). This hook is
 * kept as the single seam to revisit the policy later; it swallows the click so
 * nothing behind the backdrop reacts, but never calls onClose.
 */
export function useModalDismiss(_onClose: () => void) {
  return {
    onClick: (e: MouseEvent) => {
      if (e.target === e.currentTarget) e.stopPropagation();
    },
  };
}
